import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  LogOut, 
  Save, 
  RotateCcw, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Eye,
  EyeOff,
  Pencil,
  Check,
  X as XIcon,
  AlertTriangle,
  Database,
  FileQuestion,
  Layers,
  Sliders,
  MessageSquare,
  Info,
  BookOpen,
  FileText,
  Plus,
  Trash2,
  Calendar,
  BarChart3,
  Lightbulb,
  Building2,
  Users,
  Mail,
  Leaf,
  Link2,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAdmin } from '@/components/admin/AdminContext';
import { AdminLogin } from '@/components/admin/AdminLogin';
import { AnalyticsDashboard } from '@/components/admin/AnalyticsDashboard';
import { AdvancedAnalytics } from '@/components/admin/AdvancedAnalytics';
import { AdminNotifications } from '@/components/admin/AdminNotifications';
import { SuggestionsManager } from '@/components/admin/SuggestionsManager';
import { PlatformManager } from '@/components/admin/PlatformManager';
import { VendorManager } from '@/components/admin/VendorManager';
import { VendorEditsReview } from '@/components/admin/VendorEditsReview';
import { EmailTemplatesEditor } from '@/components/admin/EmailTemplatesEditor';
import { SustainabilityManager } from '@/components/admin/SustainabilityManager';
import { PlatformLinkRequestsManager } from '@/components/admin/PlatformLinkRequestsManager';
import { BlogManager } from '@/components/admin/BlogManager';
import { FlaggedQueriesManager } from '@/components/admin/FlaggedQueriesManager';
import { wizardQuestions } from '@/data/platforms';
import { platformsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Editable field component
const EditableField = ({ value, onSave, className }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className={cn("h-8", className)}
          autoFocus
        />
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleSave}>
          <Check className="w-4 h-4 text-success" />
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={handleCancel}>
          <XIcon className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className={className}>{value}</span>
      <Button 
        size="icon" 
        variant="ghost" 
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="w-3 h-3" />
      </Button>
    </div>
  );
};

// Question card component
const QuestionCard = ({ question, onUpdate, onToggle, onUpdateOption }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className={cn(
      'transition-opacity',
      !question.enabled && 'opacity-60'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="cursor-move text-muted-foreground hover:text-foreground">
              <GripVertical className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">Step {question.step}</Badge>
                <Badge variant="secondary" className="text-xs capitalize">{question.type}</Badge>
              </div>
              <EditableField
                value={question.title}
                onSave={(newValue) => onUpdate(question.id, { title: newValue })}
                className="font-semibold text-foreground mt-1"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={question.enabled}
              onCheckedChange={() => onToggle(question.id)}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        <EditableField
          value={question.subtitle}
          onSave={(newValue) => onUpdate(question.id, { subtitle: newValue })}
          className="text-sm text-muted-foreground"
        />
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0">
          <Separator className="mb-4" />
          <p className="text-xs font-medium text-muted-foreground mb-3">OPTIONS</p>
          <div className="space-y-2">
            {question.options.map((option) => (
              <div 
                key={option.id}
                className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <code className="text-xs bg-muted px-1 rounded">{option.id}</code>
                  <EditableField
                    value={option.label}
                    onSave={(newValue) => onUpdateOption(question.id, option.id, { label: newValue })}
                    className="text-sm"
                  />
                </div>
                <Badge variant="outline" className="text-xs">{option.icon}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Display setting row component
const DisplaySettingRow = ({ label, description, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
    <div className="flex-1 pr-4">
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

// Education section card for admin
const EducationSectionCard = ({ section, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(section.title);
  const [editContent, setEditContent] = useState(section.content);

  const handleSave = () => {
    onUpdate(section.id, { title: editTitle, content: editContent });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(section.title);
    setEditContent(section.content);
    setIsEditing(false);
  };

  return (
    <Card className={cn(!section.published && 'opacity-60')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {isEditing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="font-semibold mb-2"
              />
            ) : (
              <CardTitle className="text-base">{section.title}</CardTitle>
            )}
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">Order: {section.order}</Badge>
              <Badge variant={section.published ? 'default' : 'secondary'} className="text-xs">
                {section.published ? 'Published' : 'Draft'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={section.published}
              onCheckedChange={(checked) => onUpdate(section.id, { published: checked })}
            />
            {!isEditing && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => onDelete(section.id)}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isEditing && (
        <CardContent className="pt-0">
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="min-h-[200px] mb-4 font-mono text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
            <Button size="sm" onClick={handleSave}>Save changes</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Blog post card for admin
const BlogPostCard = ({ post, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editExcerpt, setEditExcerpt] = useState(post.excerpt);
  const [editContent, setEditContent] = useState(post.content);
  const [editAuthor, setEditAuthor] = useState(post.author || '');

  const handleSave = () => {
    onUpdate(post.id, { 
      title: editTitle, 
      excerpt: editExcerpt, 
      content: editContent,
      author: editAuthor,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditTitle(post.title);
    setEditExcerpt(post.excerpt);
    setEditContent(post.content);
    setEditAuthor(post.author || '');
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Card className={cn(!post.published && 'opacity-60')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {isEditing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="font-semibold mb-2"
                placeholder="Post title"
              />
            ) : (
              <CardTitle className="text-base">{post.title}</CardTitle>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(post.publishedAt)}
              </span>
              {post.author && <span>by {post.author}</span>}
              <Badge variant={post.published ? 'default' : 'secondary'} className="text-xs">
                {post.published ? 'Published' : 'Draft'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={post.published}
              onCheckedChange={(checked) => onUpdate(post.id, { published: checked })}
            />
            {!isEditing && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => onDelete(post.id)}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isEditing && (
        <CardContent className="pt-0 space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Author</label>
            <Input
              value={editAuthor}
              onChange={(e) => setEditAuthor(e.target.value)}
              placeholder="Author name"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Excerpt (short summary)</label>
            <Textarea
              value={editExcerpt}
              onChange={(e) => setEditExcerpt(e.target.value)}
              className="min-h-[60px] mt-1"
              placeholder="Brief summary shown in list view"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Full content</label>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[200px] mt-1 font-mono text-sm"
              placeholder="Full post content (supports **bold** formatting)"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
            <Button size="sm" onClick={handleSave}>Save changes</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// New section/post form
const AddContentForm = ({ type, onAdd, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [author, setAuthor] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) return;
    
    if (type === 'section') {
      onAdd({ title, content });
    } else {
      onAdd({ title, excerpt, content, author });
    }
    
    setTitle('');
    setContent('');
    setExcerpt('');
    setAuthor('');
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add new {type === 'section' ? 'education section' : 'blog post'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={type === 'section' ? 'Section title' : 'Post title'}
            className="mt-1"
          />
        </div>
        
        {type === 'post' && (
          <>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Author</label>
              <Input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Author name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Excerpt</label>
              <Textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief summary"
                className="min-h-[60px] mt-1"
              />
            </div>
          </>
        )}
        
        <div>
          <label className="text-xs font-medium text-muted-foreground">Content</label>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Full content (supports **bold** formatting)"
            className="min-h-[150px] mt-1 font-mono text-sm"
          />
        </div>
        
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!title.trim()}>
            Add {type === 'section' ? 'section' : 'post'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main admin dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { 
    logout, 
    questions, 
    displaySettings,
    educationSections,
    blogPosts,
    hasUnsavedChanges,
    updateQuestion,
    updateQuestionOption,
    toggleQuestionEnabled,
    updateDisplaySetting,
    updateEducationSection,
    addEducationSection,
    deleteEducationSection,
    saveChanges,
    resetToDefaults,
    initializeData,
  } = useAdmin();

  const [showAddSection, setShowAddSection] = useState(false);
  const [platformCount, setPlatformCount] = useState({ total: 0, enabled: 0 });
  const [activeTab, setActiveTab] = useState('analytics');

  // Initialize with default data
  useEffect(() => {
    initializeData(wizardQuestions);
  }, [initializeData]);

  // Fetch platform count
  useEffect(() => {
    const fetchPlatformCount = async () => {
      try {
        const platforms = await platformsApi.getAll();
        const enabled = platforms.filter(p => p.is_enabled !== false).length;
        setPlatformCount({ total: platforms.length, enabled });
      } catch (err) {
        console.error('Failed to fetch platform count:', err);
      }
    };
    fetchPlatformCount();
  }, []);

  const handleSave = () => {
    saveChanges();
  };

  const handleReset = () => {
    if (window.confirm('Reset wizard questions and education content to their original values? This will not affect vendors or suggestions.')) {
      resetToDefaults();
      // Re-initialize with defaults
      initializeData(wizardQuestions);
      toast.success('Reset complete', { description: 'Questions and education content restored to defaults' });
    }
  };

  const enabledQuestions = questions.filter(q => q.enabled).length;
  const publishedSections = educationSections.filter(s => s.published).length;
  const publishedPosts = blogPosts.filter(p => p.published).length;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Header - Mobile optimized */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground flex items-center gap-2 sm:gap-3">
                <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                Admin
              </h1>
              <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                Manage questions, platforms, education content, and blog posts
              </p>
            </div>
            
            {/* Action buttons - scrollable on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0">
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-warning border-warning whitespace-nowrap flex-shrink-0">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Unsaved
                </Badge>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/learn')} className="whitespace-nowrap flex-shrink-0">
                <BookOpen className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">View Learn</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/')} className="whitespace-nowrap flex-shrink-0">
                <Eye className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Preview</span>
              </Button>
              <AdminNotifications onTabChange={setActiveTab} />
              <Button variant="ghost" size="sm" onClick={logout} className="whitespace-nowrap flex-shrink-0">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileQuestion className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-semibold">{enabledQuestions}/{questions.length}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Questions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Database className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-semibold">{platformCount.enabled}/{platformCount.total}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Platforms</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-semibold">{publishedSections}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Education</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 sm:pt-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-semibold">{publishedPosts}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">Blog posts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main content tabs - scrollable on mobile, centered and wrapping on desktop */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
              <div className="flex justify-start sm:justify-center">
                <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 sm:w-auto sm:flex-wrap sm:h-auto sm:gap-1 sm:justify-center">
                <TabsTrigger value="analytics" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Stats</span>
                </TabsTrigger>
                <TabsTrigger value="vendors" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Users className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Vendors</span>
                </TabsTrigger>
                <TabsTrigger value="vendor-edits" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Reviews</span>
                </TabsTrigger>
                <TabsTrigger value="sustainability" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Leaf className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Sustain</span>
                </TabsTrigger>
                <TabsTrigger value="link-requests" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Link2 className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Links</span>
                </TabsTrigger>
                <TabsTrigger value="suggestions" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Lightbulb className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Suggest</span>
                </TabsTrigger>
                <TabsTrigger value="platform-mgmt" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Building2 className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Platforms</span>
                </TabsTrigger>
                <TabsTrigger value="questions" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <FileQuestion className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Q&apos;s</span>
                </TabsTrigger>
                <TabsTrigger value="education" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Learn</span>
                </TabsTrigger>
                <TabsTrigger value="blog" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Blog</span>
                </TabsTrigger>
                <TabsTrigger value="moderation" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Shield className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Moderation</span>
                </TabsTrigger>
                <TabsTrigger value="emails" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Mail className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Emails</span>
                </TabsTrigger>
                <TabsTrigger value="display" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Sliders className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Display</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-1.5 px-2 sm:px-3 whitespace-nowrap">
                  <Settings className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Settings</span>
                </TabsTrigger>
              </TabsList>
              </div>
            </div>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <AnalyticsDashboard />
              <div className="mt-8">
                <AdvancedAnalytics />
              </div>
            </TabsContent>

            {/* Vendors Tab */}
            <TabsContent value="vendors">
              <VendorManager />
            </TabsContent>

            {/* Vendor Edits Review Tab */}
            <TabsContent value="vendor-edits">
              <VendorEditsReview />
            </TabsContent>

            {/* Suggestions Tab */}
            <TabsContent value="suggestions">
              <SuggestionsManager />
            </TabsContent>

            {/* Sustainability Tab */}
            <TabsContent value="sustainability">
              <SustainabilityManager />
            </TabsContent>

            {/* Platform Link Requests Tab */}
            <TabsContent value="link-requests">
              <PlatformLinkRequestsManager />
            </TabsContent>

            {/* Platform Management Tab */}
            <TabsContent value="platform-mgmt">
              <PlatformManager />
            </TabsContent>

            <TabsContent value="questions" className="space-y-4">
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="flex items-start gap-3 py-4">
                  <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Managing Questions</p>
                    <p>
                      Edit question titles and subtitles by clicking the pencil icon. 
                      Toggle questions on/off to include or exclude them from the wizard flow.
                      Option IDs are used for matching logic and should not be changed.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {questions.map((question) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  onUpdate={updateQuestion}
                  onToggle={toggleQuestionEnabled}
                  onUpdateOption={updateQuestionOption}
                />
              ))}
            </TabsContent>

            <TabsContent value="education" className="space-y-4">
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="flex items-start gap-3 py-4">
                  <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Education Content</p>
                    <p>
                      Manage the educational sections shown on the "Learn about AI" page.
                      Content supports **bold** markdown formatting.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {!showAddSection ? (
                <Button 
                  variant="outline" 
                  onClick={() => setShowAddSection(true)}
                  className="w-full gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add new section
                </Button>
              ) : (
                <AddContentForm
                  type="section"
                  onAdd={(section) => {
                    addEducationSection(section);
                    setShowAddSection(false);
                  }}
                  onCancel={() => setShowAddSection(false)}
                />
              )}

              {educationSections
                .sort((a, b) => a.order - b.order)
                .map((section) => (
                  <EducationSectionCard
                    key={section.id}
                    section={section}
                    onUpdate={updateEducationSection}
                    onDelete={deleteEducationSection}
                  />
                ))}
            </TabsContent>

            <TabsContent value="blog">
              <BlogManager />
            </TabsContent>

            {/* Content Moderation Tab */}
            <TabsContent value="moderation">
              <FlaggedQueriesManager />
            </TabsContent>

            {/* Email Templates Tab */}
            <TabsContent value="emails">
              <EmailTemplatesEditor />
            </TabsContent>

            <TabsContent value="display" className="space-y-4">
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="flex items-start gap-3 py-4">
                  <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Display Settings</p>
                    <p>
                      Control how much reasoning and transparency is shown to users. 
                      These settings let you tune the experience without changing the underlying logic.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Reasoning & Explanations
                  </CardTitle>
                  <CardDescription>
                    Control visibility of "why" explanations throughout the app
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DisplaySettingRow
                    label="Show reasoning in results"
                    description="Display 'Why this result?' expandable sections on the results page"
                    checked={displaySettings.showReasoningInResults}
                    onCheckedChange={(checked) => updateDisplaySetting('showReasoningInResults', checked)}
                  />
                  <DisplaySettingRow
                    label="Show reasoning in platform details"
                    description="Display 'Based on your answers' suitability section on platform detail pages"
                    checked={displaySettings.showReasoningInDetails}
                    onCheckedChange={(checked) => updateDisplaySetting('showReasoningInDetails', checked)}
                  />
                  <DisplaySettingRow
                    label="Show reasoning in comparison"
                    description="Display 'Our assessment' sections (strengths, limitations) in comparison view"
                    checked={displaySettings.showReasoningInComparison}
                    onCheckedChange={(checked) => updateDisplaySetting('showReasoningInComparison', checked)}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Info className="w-5 h-5 text-primary" />
                    Data Transparency
                  </CardTitle>
                  <CardDescription>
                    Control visibility of data source indicators
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DisplaySettingRow
                    label="Show confidence indicators"
                    description="Display data source dots (vendor-provided, independently reviewed, unclear) next to comparison fields"
                    checked={displaySettings.showConfidenceIndicators}
                    onCheckedChange={(checked) => updateDisplaySetting('showConfidenceIndicators', checked)}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Settings Tab - System Settings & Reset */}
            <TabsContent value="settings" className="space-y-4">
              <Card className="bg-muted/50 border-border/50">
                <CardContent className="flex items-start gap-3 py-4">
                  <Settings className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">System Settings</p>
                    <p>
                      Configure system-wide settings, manage data, and access administrative actions.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Reset Section */}
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <RotateCcw className="w-5 h-5 text-amber-600" />
                    Reset to Defaults
                  </CardTitle>
                  <CardDescription>
                    Reset wizard questions and education content to their original values
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-amber-100/50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 mb-1">This action will:</p>
                      <ul className="text-amber-700 space-y-1 list-disc list-inside">
                        <li>Reset all wizard questions to defaults</li>
                        <li>Reset education section content to defaults</li>
                        <li>Clear any unsaved display settings changes</li>
                      </ul>
                      <p className="text-amber-700 mt-2">
                        <strong>This will NOT affect:</strong> Vendors, platforms, suggestions, blog posts, or analytics data
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    onClick={handleReset} 
                    className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to defaults
                  </Button>
                </CardContent>
              </Card>

              {/* Save Changes Section */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Save className="w-5 h-5 text-primary" />
                    Save Changes
                  </CardTitle>
                  <CardDescription>
                    Save any pending changes to questions, education content, or display settings
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {hasUnsavedChanges ? (
                        <>
                          <Badge variant="outline" className="text-warning border-warning">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Unsaved changes
                          </Badge>
                          <span className="text-sm text-muted-foreground">You have pending changes</span>
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground">All changes saved</span>
                      )}
                    </div>
                    <Button onClick={handleSave} disabled={!hasUnsavedChanges} className="gap-2">
                      <Save className="w-4 h-4" />
                      Save changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

// Main export - shows login or dashboard
export default function AdminPage() {
  const { isAuthenticated } = useAdmin();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <AdminLogin />
        </main>
        <Footer />
      </div>
    );
  }

  return <AdminDashboard />;
}
